import { api } from '@stageplottter/core';
import { ApiClient, listOf } from './client';
import type { Schema } from './client';

/** JSON object of any shape (GDPR export). */
const anyJson: Schema<unknown> = { safeParse: (data) => ({ success: true, data }) };

export type Api = ReturnType<typeof createApi>;

/** Typed REST endpoints (architecture §5). All responses are validated with core DTOs. */
export function createApi(client: ApiClient) {
  const enc = encodeURIComponent;
  return {
    client,
    config: () => client.get('/config', api.RuntimeConfig, { redirectOn401: false }),
    me: (opts: { redirectOn401?: boolean } = {}) => client.get('/me', api.Me, opts),
    exportMe: () => client.get('/me/export', anyJson),
    deleteMe: () => client.delete('/me'),
    updateMe: (body: api.UpdateMe) => client.patch('/me', body),
    logout: () => client.post('/auth/logout', undefined, undefined, { redirectOn401: false }),

    listWorkspaces: () => client.get('/workspaces', listOf(api.Workspace)),
    createWorkspace: (name: string) => client.post('/workspaces', { name }, api.Workspace),
    getWorkspace: (ws: string) => client.get(`/workspaces/${enc(ws)}`, api.Workspace),
    renameWorkspace: (ws: string, name: string) =>
      client.patch(`/workspaces/${enc(ws)}`, { name }, api.Workspace),

    listBoards: (ws: string) =>
      client.get(`/workspaces/${enc(ws)}/boards`, listOf(api.BoardSummary)),
    searchBoards: (ws: string, q: string) =>
      client.get(`/workspaces/${enc(ws)}/search`, listOf(api.SearchResult), { query: { q } }),
    listTrash: (ws: string) => client.get(`/workspaces/${enc(ws)}/trash`, listOf(api.BoardSummary)),
    createBoard: (
      ws: string,
      body: {
        name: string;
        stageWidth?: number;
        stageDepth?: number;
        template?: 'blank' | 'demo';
        templateId?: string;
      },
    ) => client.post(`/workspaces/${enc(ws)}/boards`, body, api.BoardSummary),
    getBoard: (id: string) => client.get(`/boards/${enc(id)}`, api.BoardSummary),
    renameBoard: (id: string, name: string) =>
      client.patch(`/boards/${enc(id)}`, { name }, api.BoardSummary),
    deleteBoard: (id: string) => client.delete(`/boards/${enc(id)}`),
    restoreBoard: (id: string) =>
      client.post(`/boards/${enc(id)}/restore`, undefined, api.BoardSummary),
    cloneBoard: (id: string, name?: string) =>
      client.post(`/boards/${enc(id)}/clone`, name === undefined ? {} : { name }, api.BoardSummary),
    getDocument: (id: string) => client.get(`/boards/${enc(id)}/document`, api.DocumentResponse),
    /** Portable board file download (U7, `Content-Disposition: attachment`). */
    boardExportUrl: (id: string) => client.url(`/boards/${enc(id)}/export`),

    acquireLease: (id: string, takeover = false) =>
      client.post(
        `/boards/${enc(id)}/lease`,
        takeover ? { takeover: true } : {},
        api.LeaseResponse,
      ),
    releaseLease: (id: string, keepalive = false) =>
      client.delete(`/boards/${enc(id)}/lease`, { keepalive, redirectOn401: false }),

    createUpload: (boardId: string, body: { name: string; mime: string; size: number }) =>
      client.post(`/boards/${enc(boardId)}/files`, body, api.UploadResponse),
    fileUrl: (fileId: string) => client.url(`/files/${enc(fileId)}`),
  };
}

let instance: Api | null = null;

/** App-wide API instance (tests may replace it with `setApi`). */
export function getApi(): Api {
  instance ??= createApi(new ApiClient());
  return instance;
}

export function setApi(next: Api | null): void {
  instance = next;
}
